// pages/dock-station/dock-station.component.jsx
import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import { useLocation, useNavigate, useParams } from "react-router-dom";

import { useActiveSpaceData } from "../../hooks/useActiveSpaceData.hook";

import ResizableAutoColumns from "../../components/ResizableLayouts/ResizableAutoColumns";
import ResizableAutoRows from "../../components/ResizableLayouts/ResizableAutoRows";

import DockHeader from "../../components/dock-header/dock-header.component";
import Box from "../../components/retro/box/box.component";
import Members from "../../components/members/members.component";

import AntEditor from "../../components/editor/anteditor/anteditor";
import useImageUpload from "../../hooks/useImageUpload";
import UploadOverlay from "../../components/upload/UploadOverlay";
import GoBackButton from "../../components/ui-elements/gobackbutton";

import LeadsClients from "../../components/leads-clients/leads-clients.component";
import Billings from "../../components/billings/billings.component";

import RetroButton from "../../components/retro/button/retro-button.component";

import WidgetRotator from "../../components/WidgetRotator/WidgetRotator";

import HoverDropdown from "../../components/dropdown/dropdown";

import { ReactComponent as WSGridIcon1 } from "../../assets/icons/antlogyWSGridIcon1.svg";
import { ReactComponent as WSGridIcon2 } from "../../assets/icons/antlogyWSGridIcon2.svg";
import { ReactComponent as WSGridIcon3 } from "../../assets/icons/antlogyWSGridIcon3.svg";

import { Tooltip } from "react-tooltip";

import {
  BsPeopleFill,
  BsChevronDown,
  BsFullscreen,
  BsFullscreenExit,
  BsPlusLg,
} from "react-icons/bs";

import "./dock-station.styles.scss";

import {
  db,
  fieldValue,
  updateWorkspaceBrief,
} from "../../firebase/firebase.utils";

import DockChart from "../../components/charts/DockChart.component";

import Gantt from "../../components/gantt/frappe-gantt";

import AddButton from "../../components/task-add-btn/AddButton";

const DockStation = () => {
  const { wspaceid: currentSpaceId } = useParams();

  const activeSpaceData = useActiveSpaceData(currentSpaceId);
  const users = useSelector((state) => state.user.users);
  const open = useSelector((state) => state.user.currentUser.open);
  const dispatch = useDispatch();

  const navigate = useNavigate();
  const location = useLocation();
  const currentStationId = location.pathname.split("/")[4];

  const currentUser = useSelector((state) => state.user.currentUser);

  const _svgRoadmap = useRef();
  const _chartRoadmap = useRef();
  const noteEditorRef = useRef(null);
  // Header upgrade: track the brief's workspace before allowing writes.
  const briefSpaceRef = useRef(null);

  const parentColsRef = useRef(null);
  const rowsDockRef = useRef(null);
  const colsDockRef = useRef(null);

  const [docImgFile, setDocImgFile] = useState(null);
  const [docImgFileAltText, setDocImgFileAltText] = useState("");
  const { uploading, progress, status, uploadImage } = useImageUpload({
    userId: currentUser?.uid,
    getBasePath: () => currentSpaceId ? `space/${currentSpaceId}` : "",
    getParentPath: () => `space/${currentSpaceId}`,
  });

  const [viewMode, setViewMode] = useState("Month");

  const [showBrief, setShowBrief] = useState(true);
  const [showMainToolbar, setShowMainToolbar] = useState(false);
  const [showAnalytics, setShowAnaltytics] = useState(false);

  const [editorMainToolbar, setEditorMainToolbar] = useState(false);

  const [initialValue, setInitialValue] = useState("loading...");
  const [isSaving, setIsSaving] = useState(false);

  const [workspaceBrief, setWorkspaceBrief] = useState(initialValue);
  const [showGroupBy, setShowGroupBy] = useState(false);
  const [groupBy, setGroupBy] = useState("phase");

  const [firstColumnHidden, setFirstColumnHidden] = useState(false);
  const [secondColumnHidden, setSecondColumnHidden] = useState(false);

  const [briefFullscreen, setBriefFullscreen] = useState(false);

  const [billingsWidgetHidden, setBillingsWidgetHidden] = useState(false);
  const [clientsWidgetHidden, setClientsWidgetHidden] = useState(false);
  const [membersWidgetHidden, setMembersWidgetHidden] = useState(false);
  const [roadmapWidgedHidden, setRoadmapWidgetHidden] = useState(false);
  const [peopleRowHidden, setPeopleRowHidden] = useState(false);

  const [activeBillingsTab, setActiveBillingsTab] = useState("billings");
  const [activeClientsTab, setActiveClientsTab] = useState("Lead");
  const [showProjectMembers, setShowProjectMembers] = useState(false);

  const [widths, setWidths] = useState([320, 400]);
  const [childWidths, setChildWidths] = useState([200, 200]);
  const [heights, setHeights] = useState([200, 200]);

  useEffect(() => {
    briefSpaceRef.current = null;
    setInitialValue("");
    setWorkspaceBrief("");
    let first = true;
    let previousDescription;
    const stop = db
      .collection("space")
      .doc(currentSpaceId)
      .onSnapshot((snapshot) => {
        const description = snapshot.exists
          ? snapshot.data().description || ""
          : "";
        briefSpaceRef.current = snapshot.exists ? currentSpaceId : null;
        // A title/color update must not overwrite an unsaved brief draft.
        if (first || description !== previousDescription) {
          first = false;
          previousDescription = description;
          setInitialValue(description);
          setWorkspaceBrief(description);
        }
      });
    return () => {
      briefSpaceRef.current = null;
      stop();
    };
  }, [currentSpaceId]);

  const [tasklist, setTasklist] = useState([]);
  useEffect(() => {
    //mounts
    const unsub = db
      .collection("space")
      .doc(currentSpaceId)
      .collection("stations")
      .orderBy("startdate", "asc")
      .where("startdate", "!=", null)
      .onSnapshot((querySnapshot) => {
        if (!querySnapshot.empty) {
          const array = querySnapshot.docs.map((doc) => {
            return {
              start: doc.get("startdate")?.toDate(),
              end: doc.get("enddate")?.toDate(),
              name: doc.get("name"),
              id: doc.id,
              progress: (4 / 8) * 100,
              swimlane: doc.get("phase"),
              // sub_swimlane: doc.get("id"),
              editable: true,
              start_drag: true,
              end_drag: true,
              // // type: doc.get("type"),
              // project: doc.get("project"),
              // // hideChildren: doc.get("hidechildren"),
              custom_class: "task",
              dependencies: doc.get("dependencies"),
              fromSpaceId: doc.get("fromSpaceId"),
            };
          });
          setTasklist([...array]);
          if (_chartRoadmap.current && tasklist.length < 1) {
            _chartRoadmap.current.refresh([...array]);
          }
        } else {
          // setTasklist(tasks);
          setTasklist([]);
          _chartRoadmap.current?.refresh([]);
        }
      });

    return () => {
      unsub();
    };
  }, [currentSpaceId]);

  useLayoutEffect(() => {
    if (widths[0] < 280) {
      setFirstColTo70();
      setFirstColumnHidden(true);
    } else if (widths[0] > 80) {
      setFirstColumnHidden(false);
    }
    // if (widths[1] < 180) {
    // } else if (widths[1]) {
    // }
  }, [widths]);

  useLayoutEffect(() => {
    // Billings
    if (childWidths[0] < 150) {
      setFirstChildColTo70();
      setBillingsWidgetHidden(true);
    } else if (widths[0] > 80) {
      // resetChildrenAll();
      resetFirstChildCol();
      setBillingsWidgetHidden(false);
    }
    //Leads/Clients
    if (childWidths[1] < 120) {
      set2ndChildColTo70();
      setClientsWidgetHidden(true);
    } else if (childWidths[1] > 80) {
      // resetChildrenAll();
      reset2ndChildCol();
      setClientsWidgetHidden(false);
    }
    //WS Members/Prj Members
    if (childWidths[2] < 240) {
      set3rdChildColTo70();
      setMembersWidgetHidden(true);
    } else if (childWidths[2] > 80) {
      // resetChildrenAll();
      reset3rdChildCol();
      setMembersWidgetHidden(false);
    }
  }, [childWidths]);

  useLayoutEffect(() => {
    if (heights[0] < 180) {
      setFirstRowTo70();
      setRoadmapWidgetHidden(true);
    } else {
      setRoadmapWidgetHidden(false);
    }

    if (heights[1] < 160) {
      setSecondRowTo70();
      setPeopleRowHidden(true);
    } else {
      setPeopleRowHidden(false);
    }
  }, [heights]);

  const handleTaskChange = (project, start, end) => {
    db.collection("space")
      .doc(currentSpaceId)
      .collection("stations")
      .doc(project.id)
      .update({
        startdate: start,
        enddate: end,
      });
  };

  const handleDependencyAdd = (connection) => {
    db.collection("space")
      .doc(currentSpaceId)
      .collection("stations")
      .doc(connection.to)
      .update({
        dependencies: fieldValue.arrayUnion(connection.from),
      });
  };

  const handleDependencyRemove = (connection) => {
    db.collection("space")
      .doc(currentSpaceId)
      .collection("stations")
      .doc(connection.to)
      .update({
        dependencies: fieldValue.arrayRemove(connection.from),
      });
  };

  const goToProject = (task) => {
    navigate(`/s/${task.fromSpaceId}/e/${task.id}/b`);
  };

  const handleViewModeChange = (e) => {
    setViewMode(e.target.value);
    _chartRoadmap.current.change_view_mode(e.target.value);
  };

  useEffect(() => {
    // if (chart.current) return;
    _chartRoadmap.current = new Gantt(_svgRoadmap.current, tasklist, {
      on_click: function (task) {
        console.log(task);
        goToProject(task);
      },
      on_dependency_added: function (connection) {
        console.log(connection);
        handleDependencyAdd(connection);
      },
      on_dependency_deleted: function (connection) {
        console.log(connection);
        handleDependencyRemove(connection);
      },
      on_date_change: function (task, start, end) {
        console.log(task, start, end);
        handleTaskChange(task, start, end);
      },
      on_progress_change: function (task, progress) {
        console.log(task, progress);
        // handleProgressChange(task);
      },
      on_view_change: function (mode) {
        console.log(mode);
      },
      view_mode: viewMode,
    });
  }, [tasklist]);

  // AUTOSAVE of WORKSPACE BRIEF;
  // AUTOSAVE of WORKSPACE BRIEF;
  useEffect(() => {
    if (
      briefSpaceRef.current !== currentSpaceId ||
      initialValue === workspaceBrief
    )
      return;
    const timer = setTimeout(() => {
      updateWorkspaceBrief(currentSpaceId, workspaceBrief, currentUser.uid);
    }, 3000);
    return () => clearTimeout(timer);
  }, [workspaceBrief, initialValue, currentSpaceId, currentUser.uid]);

  const handleUploadDocImage = async () => {
    const { url } = await uploadImage(docImgFile);
    return url;
  };

  const setFirstColTo70 = () => parentColsRef.current?.setWidth(0, 70);
  const setFirstColTo320 = () => parentColsRef.current?.setWidth(0, 320);
  const resetLeft = () => parentColsRef.current?.reset(0);
  const resetAll = () => parentColsRef.current?.resetAll();

  const setFirstRowTo70 = () => rowsDockRef.current?.setHeight(0, 50);
  const setFirstRowTo150 = () => rowsDockRef.current?.setHeight(0, 150);
  const setSecondRowTo70 = () => rowsDockRef.current?.setHeight(1, 50);
  const setSecondRowTo320 = () => rowsDockRef.current?.setHeight(1, 320);
  const setSecondRowTo240 = () => rowsDockRef.current?.setHeight(1, 240);
  const resetAllRows = () => rowsDockRef.current?.resetAll();

  // Billings/Expenses Widget
  const setFirstChildColTo70 = () => colsDockRef.current?.setWidth(0, 70);
  const setFirstChildColTo320 = () => colsDockRef.current?.setWidth(0, 320);
  const resetFirstChildCol = () => colsDockRef.current?.reset(0);

  // Leads/Clients Widget
  const set2ndChildColTo70 = () => colsDockRef.current?.setWidth(1, 70);
  const reset2ndChildCol = () => colsDockRef.current?.reset(1);

  // WS Members/Prj Members Widget
  const set3rdChildColTo70 = () => colsDockRef.current?.setWidth(2, 70);
  const reset3rdChildCol = () => colsDockRef.current?.reset(2);

  const resetChildrenLeft = () => colsDockRef.current?.reset(0);
  const resetChildrenAll = () => colsDockRef.current?.resetAll();

  const handleLayout1 = () => {
    setFirstColTo70();
  };

  const handleLayout2 = () => {
    setFirstColTo320();
    setSecondRowTo70();
  };

  const handleLayout3 = () => {
    setFirstColTo320();
    resetAllRows();
  };

  const handleResetFirstColumn = async () => {
    if (firstColumnHidden) {
      resetLeft();
    } else return;
  };

  const handleResetPeopleRow = () => {
    if (peopleRowHidden) {
      setSecondRowTo320();
    } else return;
  };

  const handleResetRoadmapRow = () => {
    if (roadmapWidgedHidden) {
      resetAllRows();
      setRoadmapWidgetHidden(false);
      setPeopleRowHidden(false);
    } else return;
  };

  const options = [
    {
      value: "g1",
      content: (
        <>
          <WSGridIcon1 />
        </>
      ),
    },
    {
      value: "g2",
      content: (
        <>
          <WSGridIcon2 />
        </>
      ),
    },
    {
      value: "g3",
      content: (
        <>
          <WSGridIcon3 />
        </>
      ),
    },
  ];

  const handleChangeLayout = (v) => {
    if (v === "g1") {
      handleLayout3();
    } else if (v === "g2") {
      handleLayout1();
    } else if (v === "g3") {
      setFirstColumnHidden(false);
      handleLayout2();
    }
  };

  const handleSaveForm = async () => {
    if (briefSpaceRef.current !== currentSpaceId) return;
    try {
      setIsSaving(true);
      await updateWorkspaceBrief(
        currentSpaceId,
        workspaceBrief,
        currentUser.uid,
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="dockStation">
      <UploadOverlay visible={uploading} progress={progress} status={status} />
      <div className="dockStation-content">
        {activeSpaceData ? (
          <>
            <DockHeader
              activeSpaceData={activeSpaceData}
              actions={
                <HoverDropdown
                key="dockHoverDropdown"
                  options={options}
                  onChange={handleChangeLayout}
                />
              }
            />
            {/* <div
              style={{
                marginBottom: 8,
                display: "flex",
                gap: 8,
                position: "absolute",
                right: 27,
                top: 48,
                zIndex: 100,
              }}
            >
              <button
                onClick={() => handleLayout1()}
                className="home_customgrid_icon"
              >
                <WSGridIcon2 />
              </button>
              <button
                onClick={() => handleLayout2()}
                className="home_customgrid_icon"
              >
                <WSGridIcon3 />
              </button>
              <button
                onClick={() => handleLayout3()}
                className="home_customgrid_icon"
              >
                <WSGridIcon1 />
              </button>
            </div> */}
          </>
        ) : (
          <div className="dockHeader">
            <div className="dH__upperRow">
              <GoBackButton />
              <div className="dh__header">
                {" "}
                <RetroButton style={{ background: "white" }}>
                  <BsPeopleFill />
                </RetroButton>
                <h2 style={{ fontWeight: "500", fontSize: "1.2em" }}>
                  Shared With Me
                </h2>
              </div>
            </div>
            <div className="dH__bottomRow"></div>
          </div>
        )}
        <div
          className="dockStation-content-inner"
          style={{ paddingLeft: open ? 0 : 10 }}
        >
          <ResizableAutoColumns
            ref={parentColsRef}
            fixedPx={[320, null]}
            minPx={[80, 180]}
            maxPx={[500, null]}
            gutter={15}
            autoUnlockOnOverflow={false} // optional: if fixed column overflows, it returns to auto
            className="dockstation_cols"
            onWidthsChange={({ px, pct }) =>
              // ({ px, pct }) => console.log("widths:", { px, pct })
              setWidths([...px])
            }
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
                width: "100%",
                justifyContent: firstColumnHidden ? "center" : "space-around",
              }}
              className={
                firstColumnHidden
                  ? "widget-closed-container"
                  : "widget-open-container"
              }
              onClick={() => handleResetFirstColumn()}
            >
              <WidgetRotator
                key={1}
                rotated={firstColumnHidden}
                anchor="top-left"
              >
                <div className="client__header-tabs">
                  <div
                    className={
                      !showAnalytics ? "widgetheader" : "widgetheader-nonactive"
                    }
                    style={{
                      boxShadow: firstColumnHidden && "none",
                      minWidth: 150,
                    }}
                  >
                    <h3 onClick={() => setShowAnaltytics(false)}>
                      Workspace Brief
                    </h3>
                    {!firstColumnHidden && (
                      <div
                        className="notes__form-fullscreen-btn"
                        onClick={() => {
                          // if (briefFullscreen) {
                          //   // setFirstColumnHidden(false);
                          //   handleLayout3();
                          // } else {
                          //   // setFirstColTo70();
                          //   handleLayout1();
                          //   // setFirstColumnHidden(true);
                          // }
                          setBriefFullscreen(!briefFullscreen);
                        }}
                      >
                        {briefFullscreen ? (
                          <BsFullscreenExit
                            size="1.2em"
                            data-tooltip-id="turnOffFullScreen-how"
                          />
                        ) : (
                          <BsFullscreen
                            size="1em"
                            data-tooltip-id="turnOnFullScreen-how"
                          />
                        )}
                      </div>
                    )}
                  </div>
                  <div
                    className={
                      showAnalytics ? "widgetheader" : "widgetheader-nonactive"
                    }
                    style={{ boxShadow: firstColumnHidden && "none" }}
                  >
                    <h3 onClick={() => setShowAnaltytics(true)}>Analytics</h3>
                  </div>
                </div>
              </WidgetRotator>

              {!showAnalytics && (
                <div
                  style={
                    briefFullscreen
                      ? {
                          position: "fixed",
                          top: 0,
                          right: 0,
                          bottom: 0,
                          left: 0,
                          backgroundColor: "rgba(0, 0, 0, 0.7)", // Semi-transparent black background
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          zIndex: 1000, // Ensure it's on top of other content
                        }
                      : {
                          display: "flex",
                          height: firstColumnHidden ? 0 : "100%",
                          width: "100%",
                        }
                  }
                  onClick={() => {
                    if (briefFullscreen) {
                      setBriefFullscreen(false);
                    }
                  }}
                >
                  <div
                    className="workspace__journal"
                    style={{
                      display: firstColumnHidden ? "none" : "flex",
                      height: briefFullscreen ? "calc(100% - 30px)" : "100%",
                      borderRadius: briefFullscreen
                        ? "5px 5px 5px 5px"
                        : "0px 5px 5px 5px",
                    }}
                    onClick={(e) => e.stopPropagation()} // keep clicks inside from closing
                  >
                    {briefFullscreen && (
                      <div
                        style={{
                          display: "flex",
                          width: "100%",
                          justifyContent: "space-between",
                          alignItems: "center",
                          paddingLeft: 5,
                          paddingRight: 10,
                        }}
                      >
                        <h3>Workspace Brief</h3>
                        {/* <button
                          className="journal__close"
                          onClick={() => setBriefFullscreen(false)}
                          aria-label="Close"
                          style={{height: 30}}
                        >
                          <BsPlusLg size="1.7em" />
                        </button> */}
                        <button
                          onClick={() => setBriefFullscreen(false)}
                          className="notes__form-fullscreen-btn"
                        >
                          <BsFullscreenExit
                            size="1.6em"
                            data-tooltip-id="turnOnFullScreen-how"
                            aria-label="Close"
                          />
                        </button>
                      </div>
                    )}
                    <AntEditor
                      key={currentSpaceId}
                      mainToolbarOn={briefFullscreen}
                      floatingToolbarOn={true}
                      placeholder={
                        "You can use this text area for onboarding information, for team guidelines, workflow explanations, etc..."
                      }
                      initialValue={initialValue}
                      setDocContent={setWorkspaceBrief}
                      setDocImgFile={setDocImgFile}
                      setDocImgFileAltText={setDocImgFileAltText}
                      docImgFileAltText={docImgFileAltText}
                      handleUploadDocImage={handleUploadDocImage}
                      editability={true}
                    />
                    <div
                      className="journal__form-button"
                      onClick={handleSaveForm}
                    >
                      {isSaving ? "Saving..." : "Save"}
                    </div>
                  </div>
                </div>
              )}
              {showAnalytics && (
                <DockChart
                  activeSpaceData={activeSpaceData}
                  key={currentSpaceId}
                  display={firstColumnHidden ? "none" : "flex"}
                />
              )}
            </div>
            <ResizableAutoRows
              key={activeSpaceData ? "rows-2" : "rows-1"} // optional: only if needed
              ref={rowsDockRef}
              minPx={[80, 120]} // min heights
              maxPx={[630, null]} // bottom uncapped
              gutter={15} // handle thickness
              autoUnlockOnOverflow={false}
              className="dockstation_cols"
              onHeightsChange={({ px, pct }) =>
                // console.log("heights:", { px, pct })
                setHeights([...px])
              }
            >
              <div
                className={
                  roadmapWidgedHidden
                    ? "widget-closed-container"
                    : "widget-open-container"
                }
                onClick={() => handleResetRoadmapRow()}
              >
                <div
                  className="widgetheader"
                  style={{
                    width: "100%",
                    justifyContent: roadmapWidgedHidden
                      ? "center"
                      : "flex-start",
                    boxShadow: roadmapWidgedHidden ? "none" : "none",
                  }}
                >
                  <h3>Roadmap</h3>
                </div>
                <div
                  className="roadmap__content"
                  style={{ display: roadmapWidgedHidden ? "none" : "flex" }}
                >
                  <div className="gantt__header-buttons">
                    <div className="gantt__groupby">
                      <button
                        onClick={() => setShowGroupBy(!showGroupBy)}
                        style={{ textWrapMode: "nowrap", fontWeight: 600 }}
                      >
                        Group by
                      </button>
                      <button
                        className="gantt__groupbyBtn"
                        onClick={() => setShowGroupBy(!showGroupBy)}
                      >
                        <p>{groupBy}</p>
                        <BsChevronDown />
                      </button>
                      {showGroupBy && (
                        <div className="chat_grpselect">
                          <button
                            className="chat_grpoption"
                            onClick={() =>
                              setGroupBy("projectmanager") &
                              setShowGroupBy(false)
                            }
                          >
                            Project Manager
                          </button>
                          <button
                            className="chat_grpoption"
                            onClick={() =>
                              setGroupBy("status") & setShowGroupBy(false)
                            }
                          >
                            Status
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="gantt__buttons-dock">
                      <button
                        style={{
                          textDecoration:
                            viewMode === "Day" &&
                            `underline 2px ${activeSpaceData?.color}`,
                        }}
                        className={
                          viewMode === "Day"
                            ? "gantt__button-active"
                            : "gantt__button"
                        }
                        value="Day"
                        onClick={handleViewModeChange}
                      >
                        Day
                      </button>
                      <button
                        style={{
                          textDecoration:
                            viewMode === "Week" &&
                            `underline 2px ${activeSpaceData?.color}`,
                        }}
                        className={
                          viewMode === "Week"
                            ? "gantt__button-active"
                            : "gantt__button"
                        }
                        value="Week"
                        onClick={handleViewModeChange}
                      >
                        Week
                      </button>
                      <button
                        style={{
                          textDecoration:
                            viewMode === "Month" &&
                            `underline 2px ${activeSpaceData?.color}`,
                        }}
                        className={
                          viewMode === "Month"
                            ? "gantt__button-active"
                            : "gantt__button"
                        }
                        value="Month"
                        onClick={handleViewModeChange}
                      >
                        Month
                      </button>
                    </div>
                  </div>
                  <div className="gantt-host">
                    <svg
                      ref={_svgRoadmap}
                      // style={{ maxHeight: "100%" }}
                      xmlns="http://www.w3.org/2000/svg"
                      xmlnsXlink="http://www.w3.org/1999/xlink"
                    ></svg>
                  </div>
                </div>
              </div>
              {activeSpaceData && (
                // <div className="ds__content">
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                    width: "100%",
                    justifyContent: "space-around",
                  }}
                  // className={
                  //   peopleRowHidden
                  //     ? "widget-closed-container"
                  //     : "widget-open-container"
                  // }
                  onClick={() => handleResetRoadmapRow()}
                >
                  <ResizableAutoColumns
                    ref={colsDockRef}
                    // minPx={[280, 180]}
                    maxPx={[720, null]} // null = no cap
                    gutter={15}
                    autoUnlockOnOverflow={false} // optional: if fixed column overflows, it returns to auto
                    // className="dockstation_cols"
                    onWidthsChange={({ px, pct }) =>
                      // console.log("widths:", { px, pct })
                      setChildWidths([...px])
                    }
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        height: "100%",
                        width: "100%",
                        justifyContent:
                          billingsWidgetHidden || peopleRowHidden
                            ? "center"
                            : "space-around",
                      }}
                      className={
                        billingsWidgetHidden || peopleRowHidden
                          ? "widget-closed-container"
                          : "widget-open-container"
                      }
                      onClick={() => handleResetPeopleRow()}
                    >
                      <WidgetRotator
                        key={1}
                        rotated={billingsWidgetHidden && !peopleRowHidden}
                        anchor="top-left"
                      >
                        <div className="billings__tabs">
                          <button
                            type="button"
                            className={
                              activeBillingsTab === "billings"
                                ? "widgetheader"
                                : "widgetheader-nonactive"
                            }
                            onClick={() => setActiveBillingsTab("billings")}
                            style={{
                              boxShadow:
                                (billingsWidgetHidden || peopleRowHidden) &&
                                "none",
                            }}
                          >
                            <h3>Billings</h3>
                          </button>

                          <button
                            type="button"
                            className={
                              activeBillingsTab === "expenses"
                                ? "widgetheader"
                                : "widgetheader-nonactive"
                            }
                            onClick={() => setActiveBillingsTab("expenses")}
                            style={{
                              boxShadow:
                                (billingsWidgetHidden || peopleRowHidden) &&
                                "none",
                            }}
                          >
                            <h3>Expenses</h3>
                          </button>
                        </div>
                      </WidgetRotator>
                      <Billings
                        display={
                          peopleRowHidden || billingsWidgetHidden
                            ? "none"
                            : "flex"
                        }
                        onOpenInvoice={(invoice, spaceId) =>
                          navigate(`/space/${spaceId}/invoices/${invoice.id}`)
                        }
                        onOpenExpense={(expense, spaceId) =>
                          navigate(`/space/${spaceId}/expenses/${expense.id}`)
                        }
                        activeTab={activeBillingsTab}
                      />
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        height: "100%",
                        width: "100%",
                        justifyContent:
                          clientsWidgetHidden || peopleRowHidden
                            ? "center"
                            : "space-around",
                      }}
                      className={
                        clientsWidgetHidden || peopleRowHidden
                          ? "widget-closed-container"
                          : "widget-open-container"
                      }
                      onClick={() => handleResetFirstColumn()}
                    >
                      <WidgetRotator
                        key={1}
                        rotated={clientsWidgetHidden && !peopleRowHidden}
                        anchor="top-left"
                      >
                        <div className="client__header-tabs">
                          <button
                            className={
                              activeClientsTab === "Lead"
                                ? "widgetheader"
                                : "widgetheader-nonactive"
                            }
                            onClick={() => setActiveClientsTab("Lead")}
                            style={{
                              boxShadow:
                                (clientsWidgetHidden || peopleRowHidden) &&
                                "none",
                            }}
                          >
                            <h3>Leads</h3>
                          </button>
                          <button
                            className={
                              activeClientsTab === "Client"
                                ? "widgetheader"
                                : "widgetheader-nonactive"
                            }
                            onClick={() => setActiveClientsTab("Client")}
                            style={{
                              boxShadow:
                                (clientsWidgetHidden || peopleRowHidden) &&
                                "none",
                            }}
                          >
                            <h3>Clients</h3>
                          </button>
                        </div>
                      </WidgetRotator>
                      <LeadsClients
                        currentUser={currentUser}
                        display={
                          peopleRowHidden || clientsWidgetHidden
                            ? "none"
                            : "flex"
                        }
                        activeTab={activeClientsTab}
                      />
                    </div>
                    {/* </div> */}
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        height: "100%",
                        width: "100%",
                        justifyContent:
                          membersWidgetHidden || peopleRowHidden
                            ? "center"
                            : "space-around",
                      }}
                      className={
                        membersWidgetHidden || peopleRowHidden
                          ? "widget-closed-container"
                          : "widget-open-container"
                      }
                      onClick={() => handleResetFirstColumn()}
                    >
                      <WidgetRotator
                        key={1}
                        rotated={membersWidgetHidden && !peopleRowHidden}
                        anchor="top-left"
                      >
                        <div
                          className="client__header-tabs"
                          role="tablist"
                          aria-label="Member scope"
                        >
                          <button
                            type="button"
                            role="tab"
                            aria-selected={!showProjectMembers}
                            className={`members__tab ${!showProjectMembers ? "widgetheader" : "widgetheader-nonactive"}`}
                            onClick={() => setShowProjectMembers(false)}
                            style={{
                              boxShadow:
                                (membersWidgetHidden || peopleRowHidden) &&
                                "none",
                            }}
                          >
                            <h3
                              data-tooltip-id={`workspace-members-${currentSpaceId}`}
                              data-tooltip-content="Workspace members can access this dashboard and all projects. Owners and admins retain administrative access; members inherit Editor access unless explicitly restricted."
                            >
                              Workspace Members
                            </h3>
                          </button>
                          <button
                            type="button"
                            role="tab"
                            aria-selected={showProjectMembers}
                            className={`members__tab ${showProjectMembers ? "widgetheader" : "widgetheader-nonactive"}`}
                            onClick={() => setShowProjectMembers(true)}
                            style={{
                              boxShadow:
                                (membersWidgetHidden || peopleRowHidden) &&
                                "none",
                            }}
                          >
                            <h3
                              data-tooltip-id={`project-members-${currentSpaceId}`}
                              data-tooltip-content="Project-only members can access their invited projects. Viewer + team chat includes project chat history, messaging and calls. Explicit project roles override ordinary workspace membership."
                            >
                              Project Members
                            </h3>
                          </button>
                          {/* <Tooltip
                                id={`workspace-members-${currentSpaceId}`}
                                style={{ maxWidth: 400 }}
                              />

                              <Tooltip
                                id={`project-members-${currentSpaceId}`}
                                style={{ maxWidth: 400 }}
                              /> */}
                        </div>
                      </WidgetRotator>
                      <Members
                        activeSpaceData={activeSpaceData}
                        display={
                          peopleRowHidden || membersWidgetHidden
                            ? "none"
                            : "flex"
                        }
                        showProjectMembers={showProjectMembers}
                      />
                    </div>
                  </ResizableAutoColumns>
                </div>
              )}
            </ResizableAutoRows>
          </ResizableAutoColumns>
        </div>
      </div>
      <div style={{ zIndex: 202 }}>
        <AddButton />
      </div>
    </div>
  );
};

export default DockStation;
